import { format, isValid, parseISO, subDays } from "date-fns";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useLanguage } from "../../../i18n/useLanguage";
import {
  DealerPackageMappedId,
  DealerPackagePart,
  PackageUpdateInput,
} from "../../../queries/updatePartPackageByPartId";
import { PackagePartItem } from "../../../queries/useSearchPartPackagesByVehicle";
import { useSetErrorToast } from "../../../stores/hooks/useSetErrorToast";
import { CustomPart } from "../../../stores/slices/packageEditorSlice";
import { FileUploader } from "../../../ui/FileUploader/FileUploader";
import { Input } from "../../../ui/Input/Input";
import { formatPrice } from "../../../ui/PriceDisplay/PriceDisplay";
import { Textarea } from "../../../ui/Textarea/Textarea";
import { useForm } from "../../../util/useForm";

const dateStringSchema = z
  .string()
  .refine((value) => isValid(parseISO(value)), {
    message: "form.error.invalidDate",
  })
  .transform((value) => format(parseISO(value), "yyyy-MM-dd"));

const schema = z.object({
  packageDescription: z.string().default(""),
  packageDescriptionFr: z.string().default(""),
  packageExpireDate: dateStringSchema,
  packageImageFileContents: z.string().optional(),
  packageName: z.string().nonempty({ message: "form.error.packageName" }),
  packagePartNumber: z
    .string()
    .nonempty({ message: "form.error.packageNumber" }),
  packagePrice: z
    .number({ coerce: true })
    .positive({ message: "form.error.packagePrice" })
    .refine((value) => /^\d+(\.\d{1,2})?$/.test(value.toString()), {
      message: "form.error.pricePrecision",
    }),
  packageReleaseDate: dateStringSchema,
});

const getDateWithFallback = (date: null | string, fallbackDate: Date): Date => {
  const result = new Date(date || fallbackDate);

  if (isValid(result)) {
    return result;
  }

  return fallbackDate;
};

export const PackageInfoForm: FunctionComponent<{
  advancedPrice: null | number;
  customParts: CustomPart[];
  id: string;
  mappedIds: DealerPackageMappedId[];
  onSubmit(form: PackageUpdateInput): Promise<void>;
  parts: DealerPackagePart[];
  quoteTotal: null | string;
  reset: VoidFunction;
  selectedPackage?: PackagePartItem;
  setIsSending(value: boolean): void;
}> = ({
  advancedPrice,
  customParts,
  id,
  mappedIds,
  onSubmit,
  parts,
  quoteTotal,
  reset,
  selectedPackage,
  setIsSending,
}) => {
  const [language] = useLanguage();
  const { t } = useTranslation();
  const setErrorToast = useSetErrorToast();
  const [handleForm] = useForm({
    async onSubmit({
      packageDescription,
      packageDescriptionFr,
      packageExpireDate,
      packageImageFileContents,
      packageName,
      packagePartNumber,
      packagePrice,
      packageReleaseDate,
    }) {
      if (!mappedIds.length) {
        const error = new Error(t("packageEditor.noVehiclesSelected"));
        setErrorToast(error);
        return;
      }
      setIsSending(true);
      await onSubmit({
        customParts,
        mappedIds,
        packageDescription,
        packageDescriptionFr,
        packageExpireDate,
        packageImageFileContents,
        packageImageUrl: "",
        packageName,
        packagePartNumber,
        packagePrice,
        packageReleaseDate,
        parts,
      }).finally(() => setIsSending(false));
      reset();
    },
    schema,
  });

  const primaryPart = selectedPackage?.partDetails.primaryPart;

  const packageReleaseDateDefaultValue = format(
    getDateWithFallback(
      primaryPart?.releaseDate ?? null,
      subDays(new Date(), 1)
    ),
    "yyyy-MM-dd"
  );

  const packageExpireDateDefaultValue = format(
    getDateWithFallback(
      primaryPart?.releaseDate ?? null,
      new Date("2038-01-01")
    ),
    "yyyy-MM-dd"
  );

  const displayPrice =
    selectedPackage?.partPricing.primaryPart.price["1"]?.contextList[0]
      ?.displayPrice;

  const packagePrice = displayPrice ? Number(displayPrice) : "";

  return (
    <form className="p-4 grid gap-4" id={id} noValidate onSubmit={handleForm}>
      <fieldset className="grid gap-1">
        <label className="text-xs text-ink-2 font-thin" htmlFor="package-name">
          {t("packageEditor.packageName")}
        </label>
        <Input
          defaultValue={
            selectedPackage?.partDetails.primaryPart.packageName[language] ||
            selectedPackage?.partDetails.primaryPart.packageName["en"] ||
            ""
          }
          aria-label="packageName"
          id="package-name"
          name="packageName"
        />
      </fieldset>
      <fieldset className="grid gap-1">
        <label className="text-xs text-ink-2 font-thin" htmlFor="package-image">
          {t("packageEditor.packageImage")}
        </label>
        <FileUploader
          defaultValue={
            selectedPackage?.partDetails.primaryPart.packageImageLink
          }
          dropzoneOptions={{
            accept: {
              "image/jpeg": [".jpg", ".jpeg"],
              "image/png": [".png"],
            },
            maxFiles: 1,
            multiple: false,
          }}
          label={t("aoda.inputLabel.clickToSelectFile")}
          name="packageImageFileContents"
        />
      </fieldset>
      <fieldset className="grid gap-1">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="package-description"
        >
          {t("packageEditor.packageDescription")}
        </label>
        <Textarea
          defaultValue={
            selectedPackage?.partDetails.primaryPart.packageDescription["en"]
          }
          aria-label="packageDescription"
          id="package-description"
          name="packageDescription"
        />
      </fieldset>
      <fieldset className="grid gap-1">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="package-description-fr"
        >
          {t("packageEditor.packageDescriptionFr")}
        </label>
        <Textarea
          defaultValue={
            selectedPackage?.partDetails.primaryPart.packageDescription["fr"]
          }
          aria-label="packageDescription"
          id="package-description-fr"
          lang="fr"
          name="packageDescriptionFr"
        />
      </fieldset>
      <fieldset className="grid gap-1">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="package-number"
        >
          {t("packageEditor.packageNumber")}
        </label>
        <Input
          defaultValue={
            selectedPackage?.partDetails.primaryPart.packagePartNumber
          }
          aria-label="packageNumber"
          id="package-number"
          name="packagePartNumber"
        />
      </fieldset>
      <fieldset className="grid gap-1">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="package-release-date"
        >
          {t("packageEditor.packageReleaseDate")}
        </label>
        <Input
          aria-label="packageReleaseDate"
          defaultValue={packageReleaseDateDefaultValue}
          id="package-release-date"
          name="packageReleaseDate"
          type="date"
        />
      </fieldset>
      <fieldset className="grid gap-1">
        <label
          className="text-xs text-ink-2 font-thin"
          htmlFor="package-expire-date"
        >
          {t("packageEditor.packageExpireDate")}
        </label>
        <Input
          aria-label="packageExpireDate"
          defaultValue={packageExpireDateDefaultValue}
          id="package-expire-date"
          name="packageExpireDate"
          type="date"
        />
      </fieldset>
      {quoteTotal && (
        <fieldset className="grid gap-1">
          <span className="text-xs text-ink-2 font-thin">
            {t("packageEditor.quoteTotal")}
          </span>
          <Input
            aria-label="quoteTotal"
            disabled
            value={formatPrice(quoteTotal, language)}
          />
        </fieldset>
      )}
      {advancedPrice && (
        <fieldset className="grid gap-1">
          <span className="text-xs text-ink-2 font-thin">
            {t("packageEditor.advancedPricing")}
          </span>
          <Input
            aria-label="advancedPrice"
            disabled
            value={formatPrice(advancedPrice, language)}
          />
        </fieldset>
      )}
      <fieldset className="grid gap-1">
        <label className="text-xs text-ink-2 font-thin" htmlFor="package-price">
          {t("packageEditor.packagePrice")}
        </label>
        <Input
          aria-label="packagePrice"
          before="$"
          defaultValue={packagePrice}
          id="package-price"
          inputMode="numeric"
          key={packagePrice}
          name="packagePrice"
        />
      </fieldset>
    </form>
  );
};
